// ═══════════════════════════════════════════════════════════════════════════════
// Post-login / post-register navigation (pairing → consent → main app)
// ═══════════════════════════════════════════════════════════════════════════════

import type { UserProfile } from '@fdh/shared';
import { USER_ROLE } from '@fdh/shared';

const isPaired = (user: UserProfile): boolean =>
  Boolean(user.pairedAt && user.pairedAt.trim() !== '');

const hasConsented = (user: UserProfile): boolean =>
  Boolean(user.consentedAt && user.consentedAt.trim() !== '');

/** Default landing path after successful authentication, based on pairing + consent. */
export const getPostAuthHomePath = (user: UserProfile): string => {
  if (!user.emailVerified) {
    if (!isPaired(user)) {
      return user.role === USER_ROLE.EMPLOYER ? '/pairing/generate-code' : '/pairing/join';
    }
    return '/pairing/status';
  }
  if (!hasConsented(user)) {
    return '/consent';
  }
  if (!isPaired(user)) {
    return user.role === USER_ROLE.EMPLOYER ? '/dashboard' : '/pairing/join';
  }
  return user.role === USER_ROLE.EMPLOYER ? '/dashboard' : '/home';
};

const publicAuthPaths = new Set([
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/auth/google/callback',
]);

/** Strip query/hash so `/register?googleRegister=1` is treated as a public auth path. */
const normalizePathForAuthRedirect = (path: string): string => {
  const trimmed = path.trim();
  if (!trimmed) {
    return '/';
  }
  const withoutHash = trimmed.split('#')[0] ?? trimmed;
  const withoutQuery = withoutHash.split('?')[0] ?? withoutHash;
  return withoutQuery || '/';
};

const isPublicAuthPath = (path: string): boolean =>
  publicAuthPaths.has(normalizePathForAuthRedirect(path));

/**
 * If the user opened a deep link while logged out, return that path only when
 * onboarding (pairing + consent) is already satisfied.
 */
export const getRedirectAfterAuth = (
  user: UserProfile,
  fromPath?: string
): string => {
  const home = getPostAuthHomePath(user);
  if (!fromPath || isPublicAuthPath(fromPath)) {
    return home;
  }
  const employerMayUseAppUnpaired =
    user.role === USER_ROLE.EMPLOYER && user.emailVerified && hasConsented(user);
  if (
    (!isPaired(user) && !employerMayUseAppUnpaired) ||
    !user.emailVerified ||
    !hasConsented(user)
  ) {
    return home;
  }
  return fromPath;
};
